import { useMemo, useState } from 'react'
import type { CouplePerformance, PerformanceCategory } from '@/utils/api'

type FilterType = 'All' | 'Strength' | 'Weakness'

interface PerformanceItem {
  category: string
  score: number
  maxScore: number
  riskLevel: string
  type: 'strength' | 'weakness'
}

interface PerformanceTabProps {
  data?: CouplePerformance | null
}

const normalizeRiskLevel = (level?: string | null) => (level ?? '').toLowerCase()

const mapRiskLabel = (level?: string | null) => {
  const normalized = normalizeRiskLevel(level)
  if (normalized.includes('healthy') || normalized.includes('low')) return 'Healthy Foundations'
  if (normalized.includes('moderate') || normalized.includes('some')) return 'Some Attention Needed'
  if (normalized.includes('high') || normalized.includes('serious')) return 'Serious Concerns'
  return 'Unknown'
}

const mapPerformanceItems = (items?: PerformanceCategory[] | null): PerformanceItem[] =>
  (items ?? []).map((item) => ({
    category: item.label || item.category || 'Category',
    score: item.score ?? 0,
    maxScore: item.maxScore ?? 0,
    riskLevel: item.riskLabel || mapRiskLabel(item.riskLevel),
    type: item.classification === 'strength' ? 'strength' : 'weakness',
  }))

// Risk level icon component
const RiskIcon = ({ riskLevel }: { riskLevel: string }) => {
  const normalized = normalizeRiskLevel(riskLevel)

  if (normalized.includes('healthy') || normalized.includes('low')) {
    return (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='#0ecf5b'>
        <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'/>
      </svg>
    )
  }
  if (normalized.includes('some') || normalized.includes('moderate')) {
    return (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='#ffb319'>
        <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'/>
      </svg>
    )
  }
  return (
    <svg width='24' height='24' viewBox='0 0 24 24' fill='#ff7043'>
      <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'/>
    </svg>
  )
}

// Score icon component
const ScoreIcon = ({ riskLevel }: { riskLevel: string }) => {
  const normalized = normalizeRiskLevel(riskLevel)
  let color = '#0ecf5b'
  if (normalized.includes('some') || normalized.includes('moderate')) {
    color = '#ffb319'
  } else if (normalized.includes('high') || normalized.includes('serious')) {
    color = '#ff7043'
  }

  return (
    <svg width='24' height='24' viewBox='0 0 24 24' fill={color}>
      <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'/>
    </svg>
  )
}

export default function PerformanceTab({ data }: PerformanceTabProps) {
  const [manFilter, setManFilter] = useState<FilterType>('All')
  const [womanFilter, setWomanFilter] = useState<FilterType>('All')

  const manPerformance = useMemo(
    () => mapPerformanceItems(data?.personOne?.categories),
    [data?.personOne?.categories]
  )
  const womanPerformance = useMemo(
    () => mapPerformanceItems(data?.personTwo?.categories),
    [data?.personTwo?.categories]
  )

  const getRiskLevelColor = (level: string) => {
    const normalized = normalizeRiskLevel(level)
    if (normalized.includes('healthy') || normalized.includes('low')) {
      return 'text-[#0ecf5b]'
    }
    if (normalized.includes('some') || normalized.includes('moderate')) {
      return 'text-[#ffb319]'
    }
    return 'text-[#ff7043]'
  }

  const filterItems = (items: PerformanceItem[], filter: FilterType) => {
    if (filter === 'All') return items
    return items.filter((item) =>
      filter === 'Strength' ? item.type === 'strength' : item.type === 'weakness'
    )
  }

  const FilterButtons = ({ selected, onChange }: { selected: FilterType; onChange: (filter: FilterType) => void }) => (
    <div className='flex gap-[7px]'>
      {(['All', 'Strength', 'Weakness'] as FilterType[]).map((filter) => (
        <button
          key={filter}
          onClick={() => onChange(filter)}
          className={`px-4 py-1 rounded-[17px] text-[12px] font-normal transition-colors ${
            selected === filter
              ? 'bg-[#FF6600] text-white'
              : 'bg-[rgba(235,235,235,0.88)] text-[#434343] border border-dashed border-[rgba(108,108,108,0.6)]'
          }`}
        >
          {filter}
        </button>
      ))}
    </div>
  )

  const PerformanceCard = ({ item }: { item: PerformanceItem }) => (
    <div className='bg-[rgba(243,244,246,0.45)] rounded-[12px] px-7 py-2'>
      <div className='flex flex-col gap-[5px]'>
        <h3 className='text-[18px] font-bold text-black leading-[28px]'>{item.category}</h3>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-[10px]'>
            <ScoreIcon riskLevel={item.riskLevel} />
            <span className='text-[14px] font-bold text-[#151515]'>
              Score: {item.score}/{item.maxScore}
            </span>
          </div>
          <div className='flex items-center gap-2'>
            <RiskIcon riskLevel={item.riskLevel} />
            <span className={`text-[14px] ${getRiskLevelColor(item.riskLevel)}`}>
              <span className='font-normal'>Risk Level: </span>
              <span className='font-bold'>{item.riskLevel}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
      {/* Man Section */}
      <div className='flex flex-col gap-4'>
        <div className='flex items-center justify-between'>
          <h2 className='text-[18px] font-bold text-[#151515] leading-[28px]'>Man</h2>
          <FilterButtons selected={manFilter} onChange={setManFilter} />
        </div>
        <div className='bg-white border border-[#e8e8e8] rounded-[10px] p-5'>
          <div className='flex flex-col gap-[19px] max-h-[604px] overflow-y-auto'>
            {filterItems(manPerformance, manFilter).length === 0 && (
              <p className='text-[14px] text-[#4e4d4d]'>No assessment data yet.</p>
            )}
            {filterItems(manPerformance, manFilter).map((item, index) => (
              <PerformanceCard key={`${item.category}-${index}`} item={item} />
            ))}
          </div>
        </div>
      </div>

      {/* Woman Section */}
      <div className='flex flex-col gap-4'>
        <div className='flex items-center justify-between'>
          <h2 className='text-[18px] font-bold text-[#151515] leading-[28px]'>Woman</h2>
          <FilterButtons selected={womanFilter} onChange={setWomanFilter} />
        </div>
        <div className='bg-white border border-[#e8e8e8] rounded-[10px] p-5'>
          <div className='flex flex-col gap-[19px] max-h-[604px] overflow-y-auto'>
            {filterItems(womanPerformance, womanFilter).length === 0 && (
              <p className='text-[14px] text-[#4e4d4d]'>No assessment data yet.</p>
            )}
            {filterItems(womanPerformance, womanFilter).map((item, index) => (
              <PerformanceCard key={`${item.category}-${index}`} item={item} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
